import React, { useState, useEffect, useContext } from "react";
import { Table, Button, Spin, Card, Tag, DatePicker } from "antd";
import axios from "axios";
import * as XLSX from "xlsx";
import { AuthContext } from "../../context/AuthContext";
import { DownloadOutlined } from "@ant-design/icons";

const { RangePicker } = DatePicker;

export default function EmployeeAttendanceReport() {
    const { authData, backUrl } = useContext(AuthContext);
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);

    const [departments, setDepartments] = useState([]);
    const [designations, setDesignations] = useState([]);
    const [organizations, setOrganizations] = useState([]);

    const [organisation, setOrganisation] = useState("");
    const [department, setDepartment] = useState("");
    const [designation, setDesignation] = useState("");

    const [dateRange, setDateRange] = useState([]);
    const [fromDate, setFromDate] = useState("2025-11-13");
    const [toDate, setToDate] = useState("2025-11-13");

    const fetchOrganizations = async () => {
        try {
            const res = await axios.get(`${backUrl}/api/emp/organisations/`);
            setOrganizations(res.data.results);
        } catch (err) {
            console.error("Failed to fetch organizations:", err);
        }
    };

    const fetchDepartments = async (oid) => {
        try {
            const res = await axios.get(`${backUrl}/api/emp/departments/?organisation=${oid}`);
            setDepartments(res.data.results);
        } catch (err) {
            console.error("Failed to fetch departments:", err);
        }
    };

    const fetchDesignations = async (departmentId) => {
        try {
            const res = await axios.get(`${backUrl}/api/emp/designations/?department=${departmentId}`);
            setDesignations(res.data.results);
        } catch (err) {
            console.error("Error fetching designations", err);
        }
    };

    useEffect(() => {
        fetchOrganizations();
        fetchData();
    }, []);

    const fetchData = async () => {
        setLoading(true);
        try {
            const query = new URLSearchParams();
            query.append("from_date", fromDate);
            query.append("to_date", toDate);
            if (organisation) query.append("organisation", organisation);
            if (department) query.append("department", department);
            if (designation) query.append("designation", designation);

            const res = await axios.get(
                `${backUrl}/api/hr/employee-attendance-summary/?${query.toString()}`,
                {
                    headers: {
                        Authorization: `Bearer ${authData?.access}`,
                    },
                }
            );
            setData(res.data);
        } catch (err) {
            console.error(err);
        }
        setLoading(false);
    };

    // Utility: generate inclusive date list "YYYY-MM-DD"
    const getDatesBetween = (start, end) => {
        const s = new Date(start);
        const e = new Date(end);
        const arr = [];
        for (let d = new Date(s); d <= e; d.setDate(d.getDate() + 1)) {
            const year = d.getFullYear();
            const month = String(d.getMonth() + 1).padStart(2, "0");
            const day = String(d.getDate()).padStart(2, "0");
            arr.push(`${year}-${month}-${day}`);
        }
        return arr;
    };

    // Map status to short code shown in register
    const statusShort = (status) => {
        if (!status) return "-";
        const s = String(status).toUpperCase();
        if (s.includes("PRESENT")) return "P";
        if (s.includes("ABSENT")) return "A";
        if (s.includes("SUNDAY")) return "S";
        if (s.includes("HALFDAY") || s.includes("HALF_DAY") || s.includes("HALF DAY"))
            return "HD";
        if (s.includes("LEAVE")) return "L";
        if (s.includes("HOLIDAY")) return "H";
        return status; // fallback: show original
    };

    // 📌 Export complex register Excel (full-format)
    const exportExcel = () => {
        // Dates for columns (fromDate..toDate)
        const dates = getDatesBetween(fromDate, toDate);
        // Build header rows as arrays (AOA)
        const title = ["ATTENDANCE REGISTER"];
        const subHeader = [
            `Organisation: ${organizations.find((o) => String(o.id) === String(organisation))?.name || organisation || ""
            }    Period: ${fromDate} to ${toDate}`,
        ];

        // Build column headers: fixed columns + date columns + summary columns
        const fixedCols = ["SNO", "EMP ID", "EMP CODE", "NAME", "SITE", "COMPANY"];
        const dateCols = dates.map((d) => {
            const parts = d.split("-");
            return parts[2]; // day number as header (e.g., "01")
        });
        const summaryCols = ["PR", "L", "LOP", "NW", "OT", "TOTAL"];

        const headerRow = [...fixedCols, ...dateCols, ...summaryCols];

        // Create rows for each employee
        const rows = [];

        // Add header rows first (we will merge them)
        rows.push(title);
        rows.push(subHeader);
        rows.push(headerRow);

        // For each employee, create a row matching headerRow length
        data.forEach((emp, idx) => {
            const row = new Array(fixedCols.length + dates.length + summaryCols.length).fill("");

            // Fixed columns
            row[0] = idx + 1; // SNO
            row[1] = emp.employee_id ?? "";
            row[2] = emp.employee_code ?? "";
            row[3] = emp.employee_name ?? "";
            // site/company fields may not exist in payload; try fallbacks
            row[4] = emp.site ?? ""; // if your API provides site
            row[5] = emp.organisation ?? emp.company ?? "";

            // Build a map by date for quick lookup
            const mapByDate = {};
            (emp.attendance || []).forEach((att) => {
                if (att && att.date) mapByDate[att.date] = att;
            });

            // Day columns
            let prCount = 0;
            let lCount = 0;
            let lopCount = 0;
            let nwCount = 0;
            let otCount = 0;

            dates.forEach((d, di) => {
                const att = mapByDate[d];
                const colIndex = fixedCols.length + di;
                if (!att) {
                    row[colIndex] = ""; // no data
                } else {
                    const short = statusShort(att.status);
                    row[colIndex] = short;

                    // simple counts
                    const s = String(att.status).toUpperCase();
                    if (s.includes("PRESENT")) prCount++;
                    if (s.includes("HALFDAY") || s.includes("LEAVE")) lCount++;
                    if (s.includes("ABSENT")) lopCount++;
                    // NW / OT not computable without additional fields; keep 0
                    if (att.overtime) {
                        // if your att has overtime numeric value
                        otCount += Number(att.overtime) || 0;
                    }
                    // NW placeholder - if att.nw true
                    if (att.nw) nwCount++;
                }
            });

            // Summary columns start index
            const summaryStart = fixedCols.length + dates.length;
            row[summaryStart + 0] = prCount;
            row[summaryStart + 1] = lCount;
            row[summaryStart + 2] = lopCount;
            row[summaryStart + 3] = nwCount;
            row[summaryStart + 4] = otCount;
            row[summaryStart + 5] = prCount + lCount + lopCount; // TOTAL (simple)

            rows.push(row);
        });

        // Build worksheet from AOA
        const ws = XLSX.utils.aoa_to_sheet(rows);

        // Merges: merge title row across full width, and subheader too
        const totalCols = headerRow.length;
        const merges = [
            { s: { r: 0, c: 0 }, e: { r: 0, c: totalCols - 1 } }, // title (row 0)
            { s: { r: 1, c: 0 }, e: { r: 1, c: totalCols - 1 } }, // subheader (row 1)
        ];
        ws["!merges"] = merges;

        // Set column widths (rough)
        ws["!cols"] = [
            { wch: 6 }, // SNO
            { wch: 10 }, // EMP ID
            { wch: 14 }, // EMP CODE
            { wch: 22 }, // NAME
            { wch: 18 }, // SITE
            { wch: 24 }, // COMPANY
            // day cols - approximate
            ...Array(dates.length).fill({ wch: 6 }),
            // summary cols
            { wch: 6 },
            { wch: 6 },
            { wch: 6 },
            { wch: 6 },
            { wch: 6 },
            { wch: 8 },
        ];

        // Optional: make header cells bold by setting a simple style (limited in xlsx)
        // Note: full styling requires additional libraries or exceljs. xlsx supports only limited cell objects.
        const setCellBold = (r, c) => {
            const address = XLSX.utils.encode_cell({ r, c });
            if (!ws[address]) ws[address] = { t: "s", v: "" };
            ws[address].s = { font: { bold: true } }; // may be ignored by some viewers
        };

        // Bold the header row (row index 2)
        for (let c = 0; c < totalCols; c++) {
            setCellBold(2, c);
        }
        // Bold title row
        for (let c = 0; c < totalCols; c++) setCellBold(0, c);

        // Set worksheet range
        ws["!ref"] = XLSX.utils.encode_range({
            s: { r: 0, c: 0 },
            e: { r: rows.length - 1, c: totalCols - 1 },
        });

        // Create workbook and write file
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "AttendanceRegister");
        XLSX.writeFile(wb, `AttendanceRegister_${fromDate}_to_${toDate}.xlsx`);
    };

    const exportExcelSimple = () => {
        // fallback simple export if needed
        const exportRows = [];

        data.forEach((emp) => {
            const summaryRow = {
                Employee_Code: emp.employee_code,
                Employee_Name: emp.employee_name,
                Organisation: emp.organisation,
                Department: emp.department,
                Designation: emp.designation,
                Casual_Balance: emp.leave_balance?.casual,
                Sick_Balance: emp.leave_balance?.sick,
                Earned_Balance: emp.leave_balance?.earned,
                CompOff_Balance: emp.leave_balance?.compoff,
            };
            exportRows.push(summaryRow);
        });

        const ws = XLSX.utils.json_to_sheet(exportRows);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Attendance Summary");
        XLSX.writeFile(wb, "EmployeeAttendanceReport_simple.xlsx");
    };

    const columns = [
        { title: "Employee Code", dataIndex: "employee_code", fixed: "left", width: 130 },
        { title: "Employee Name", dataIndex: "employee_name", width: 200, render: (text) => <b>{text}</b> },
        { title: "Org", dataIndex: "organisation", width: 200 },
        { title: "Dept", dataIndex: "department", width: 150 },
        { title: "Designation", dataIndex: "designation", width: 150 },
        { title: "Casual", render: (r) => r.leave_balance?.casual, width: 80 },
        { title: "Sick", render: (r) => r.leave_balance?.sick, width: 80 },
        { title: "Earned", render: (r) => r.leave_balance?.earned, width: 80 },
        { title: "WeekOff", render: (r) => r.leave_balance?.compoff, width: 90 },
    ];

    const expandedRowRender = (record) => {
        // Filter attendance based on selected range
        let filteredAttendance = record.attendance || [];

        if (fromDate && toDate) {
            filteredAttendance = filteredAttendance.filter((att) => {
                return att.date >= fromDate && att.date <= toDate;
            });
        }

        return (
            <div style={{ background: "#ffffffff", padding: 10, borderRadius: 6 }}>
                <h5>Attendance</h5>

                <Table
                    dataSource={filteredAttendance}
                    rowKey={(row) => row.date}
                    size="small"
                    pagination={false}
                    bordered
                    style={{ marginBottom: 20 }}
                    columns={[
                        { title: "Date", dataIndex: "date" },
                        { title: "Status", dataIndex: "status" },
                        {
                            title: "Total Time",
                            dataIndex: "total_time",
                            render: (value) => (value && value !== "" ? value : "-"),
                        },
                        {
                            title: "Sunday",
                            dataIndex: "is_sunday",
                            render: (v) => (v ? <Tag color="gold">Yes</Tag> : "No"),
                        },
                        {
                            title: "Holiday",
                            dataIndex: "is_holiday",
                            render: (v) => (v ? <Tag color="green">Yes</Tag> : "No"),
                        },
                    ]}
                />

                <h5>Leave Taken</h5>
                <Table
                    dataSource={record.leave_taken_monthly}
                    pagination={false}
                    size="small"
                    bordered
                    rowKey={(row) => row.month}
                    columns={[
                        { title: "Month", dataIndex: "month" },
                        { title: "Casual", dataIndex: "Casual" },
                        { title: "Sick", dataIndex: "Sick" },
                        { title: "Earned", dataIndex: "Earned" },
                    ]}
                />
            </div>
        );
    };

    return (
        <>
            <center>
                <h5>Employee Attendance Report</h5>
            </center>
            <br />

            <div style={{ display: "flex", gap: 15, marginBottom: 20 }}>
                <RangePicker
                    style={{ width: 260 }}
                    onChange={(values) => {
                        setDateRange(values);
                        if (values) {
                            setFromDate(values[0].format("YYYY-MM-DD"));
                            setToDate(values[1].format("YYYY-MM-DD"));
                        }
                    }}
                    value={dateRange}
                />

                <select
                    className="form-control"
                    style={{ width: 200 }}
                    value={organisation}
                    onChange={(e) => {
                        const oid = e.target.value;
                        setOrganisation(oid);
                        setDepartment("");
                        setDesignation("");
                        fetchDepartments(oid);
                    }}
                >
                    <option value="">Select Organisation</option>
                    {organizations.map((o) => (
                        <option key={o.id} value={o.id}>
                            {o.name}
                        </option>
                    ))}
                </select>

                <select
                    className="form-control"
                    style={{ width: 200 }}
                    value={department}
                    disabled={!organisation}
                    onChange={(e) => {
                        const dep = e.target.value;
                        setDepartment(dep);
                        setDesignation("");
                        fetchDesignations(dep);
                    }}
                >
                    <option value="">Select Department</option>
                    {departments.map((d) => (
                        <option key={d.id} value={d.id}>
                            {d.name}
                        </option>
                    ))}
                </select>

                <select
                    className="form-control"
                    style={{ width: 200 }}
                    value={designation}
                    disabled={!department}
                    onChange={(e) => setDesignation(e.target.value)}
                >
                    <option value="">Select Designation</option>
                    {designations.map((d) => (
                        <option key={d.id} value={d.id}>
                            {d.name}
                        </option>
                    ))}
                </select>

                <Button
                    type="primary"
                    onClick={fetchData}
                    style={{
                        backgroundColor: "#0e497b",
                        borderColor: "#0e497b",
                        color: "white",
                    }}
                >
                    Apply
                </Button>
            </div>

            <Button
                type="primary"
                onClick={exportExcel}
                icon={<DownloadOutlined />}
                style={{
                    marginBottom: 15,
                    backgroundColor: "#0e497b",
                    borderColor: "#0e497b",
                    color: "white",
                }}
            >
                Download Excel (Register format)
            </Button>

            <Button
                type="default"
                onClick={exportExcelSimple}
                style={{ marginLeft: 12, marginBottom: 15 }}
            >
                Download Simple Excel
            </Button>

            {loading ? (
                <Spin size="large" />
            ) : (
                <Table
                    dataSource={data}
                    columns={columns}
                    expandable={{ expandedRowRender }}
                    rowKey={(row) => row.employee_id}
                    bordered
                    size="middle"
                    scroll={{ x: 1400 }}
                    components={{
                        header: {
                            cell: (props) => (
                                <th {...props} style={{ backgroundColor: "#0e497b", color: "white" }}>
                                    {props.children}
                                </th>
                            ),
                        },
                    }}
                />
            )}
        </>
    );
}
